import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, Send, Check, ChevronsUpDown, PackageSearch, Loader2, Pencil, PackageCheck, RotateCcw, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import HistoryStamps from "@/components/HistoryStamps";
import { todayStr, fmtDay } from "@/lib/utils";

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const statusCfg = {
  Pending: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300",
  Issued: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950 dark:text-blue-300",
  Received: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300",
  Rejected: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300",
};

export default function MaterialRequestPanel() {
  const { user } = useAuth();
  const defaultStore = user?.location && ["MIHAN", "MANEWADA"].includes(user.location) ? user.location : "MIHAN";
  const [store, setStore] = useState(defaultStore);

  useEffect(() => {
    if (user?.role !== "cafeteria") return;
    api.get("/cafeterias").then((r) => {
      const mine = r.data.find((c) => c.name.trim() === (user.location || "").trim());
      const mapped = mine && (mine.default_store || mine.default_base_kitchen);
      if (mapped) setStore(mapped);
    }).catch(() => {});
  }, [user]);
  const [materials, setMaterials] = useState([]);
  const [rows, setRows] = useState([{ material_id: "", qty: "" }]);
  const [remarks, setRemarks] = useState("");
  const [requiredBy, setRequiredBy] = useState(todayStr());
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editReq, setEditReq] = useState(null);
  const [editMats, setEditMats] = useState([]);
  const [editRows, setEditRows] = useState([]);
  const [editRemarks, setEditRemarks] = useState("");
  const [editRequiredBy, setEditRequiredBy] = useState(todayStr());
  const [editSaving, setEditSaving] = useState(false);

  const loadMaterials = (s) => api.get("/materials", { params: { store: s } }).then((r) => setMaterials(r.data)).catch(() => {});
  const loadRequests = () => api.get("/material-requests").then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => { loadMaterials(store); }, [store]);
  useEffect(() => { loadRequests(); const t = setInterval(loadRequests, 15000); return () => clearInterval(t); }, []);

  const matById = useMemo(() => Object.fromEntries(materials.map((m) => [m.id, m])), [materials]);

  const totals = useMemo(() => {
    let issued = 0, received = 0;
    for (const r of requests) for (const it of r.items) {
      issued += (it.issued_qty || 0) * (it.rate || 0);
      received += (it.received_qty || 0) * (it.rate || 0);
    }
    return { issued, received };
  }, [requests]);

  const submit = async () => {
    const items = rows.filter((r) => r.material_id && Number(r.qty) > 0).map((r) => {
      const m = matById[r.material_id];
      return { material_id: m.id, name: m.name, unit: m.unit, qty: Number(r.qty), rate: m.rate };
    });
    if (!items.length) return toast.error("Add at least one material with quantity");
    if (!requiredBy) return toast.error("Select the required-by date");
    setLoading(true);
    try {
      await api.post("/material-requests", { store, items, remarks, required_by: requiredBy });
      toast.success("Material request sent to store");
      setRows([{ material_id: "", qty: "" }]);
      setRemarks("");
      loadRequests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const downloadTemplate = async (fmt) => {
    try {
      const r = await api.get("/material-requests/template", { params: { store, fmt }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `baron_material_request_${store}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const uploadSheet = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/material-requests/parse", fd, { params: { store } });
      if (!data.items.length) {
        toast.error("No materials with quantity found in the sheet");
      } else {
        setRows(data.items.map((it) => ({ material_id: it.material_id, qty: String(it.qty) })));
        toast.success(`${data.items.length} material(s) loaded — review and press Send Request`);
      }
      if (data.unmatched.length) {
        toast.warning(`Not found in ${store} Store: ${data.unmatched.slice(0, 5).join(", ")}${data.unmatched.length > 5 ? ` +${data.unmatched.length - 5} more` : ""}`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const repeatLast = () => {
    const last = requests[0];
    setStore(last.store);
    setRows(last.items.map((it) => ({ material_id: it.material_id, qty: String(it.qty) })));
    setRemarks(last.remarks || "");
    toast.success(`Filled from last request (${new Date(last.created_at).toLocaleDateString("en-IN")})`);
  };

  const openEdit = async (r) => {
    setEditReq(r);
    setEditRemarks(r.remarks || "");
    setEditRequiredBy(r.required_by || todayStr());
    setEditRows(r.items.map((it) => ({ material_id: it.material_id, qty: String(it.qty) })));
    try {
      const res = await api.get("/materials", { params: { store: r.store } });
      setEditMats(res.data);
    } catch { /* keep empty list */ }
  };

  const saveEdit = async () => {
    const items = editRows.filter((r) => r.material_id && Number(r.qty) > 0).map((r) => {
      const m = editMats.find((x) => x.id === r.material_id);
      const prev = editReq.items.find((x) => x.material_id === r.material_id);
      return { material_id: r.material_id, name: m ? m.name : prev?.name, unit: m ? m.unit : prev?.unit, qty: Number(r.qty), rate: m ? m.rate : (prev?.rate ?? 0) };
    });
    if (!items.length) return toast.error("Add at least one material with quantity");
    setEditSaving(true);
    try {
      await api.put(`/material-requests/${editReq.id}`, { store: editReq.store, items, remarks: editRemarks, required_by: editRequiredBy });
      toast.success("Request updated");
      setEditReq(null);
      loadRequests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6 border-orange-100 dark:border-slate-800 h-fit">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <PackageSearch className="h-5 w-5 text-primary" /> Request Material from Store
          </h3>
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={repeatLast} disabled={!requests.length} data-testid="mreq-repeat-last-button">
            <RotateCcw className="h-3.5 w-3.5" /> Repeat last request
          </Button>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Store</Label>
            <Select value={store} onValueChange={setStore}>
              <SelectTrigger data-testid="mreq-store-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MIHAN">MIHAN Store</SelectItem>
                <SelectItem value="MANEWADA">MANEWADA Store</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-lg border border-dashed border-border p-3 space-y-2" data-testid="mreq-bulk-upload">
            <p className="text-xs text-muted-foreground">Bulk request: download the template (lists all {store} Store materials), fill the <span className="font-mono">qty</span> column, then upload.</p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => downloadTemplate("xlsx")} data-testid="mreq-template-xlsx-button"><Download className="h-3.5 w-3.5" /> Excel Template</Button>
              <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => downloadTemplate("csv")} data-testid="mreq-template-csv-button"><Download className="h-3.5 w-3.5" /> CSV Template</Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={uploadSheet} data-testid="mreq-upload-input" />
              <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="mreq-upload-button">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload Excel/CSV
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Required By</Label>
            <Input type="date" min={todayStr()} value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} className="w-48 font-mono" data-testid="mreq-required-by-input" />
          </div>
          <div className="space-y-3">
            <Label>Materials</Label>
            {rows.map((row, idx) => {
              const m = matById[row.material_id];
              return (
                <div key={idx} className="flex flex-wrap sm:flex-nowrap items-end gap-2" data-testid="mreq-item-row">
                  <div className="basis-full sm:basis-0 sm:flex-1 min-w-0">
                    <MaterialCombobox
                      materials={materials}
                      value={row.material_id}
                      onChange={(v) => setRows(rows.map((r, i) => (i === idx ? { ...r, material_id: v } : r)))}
                    />
                  </div>
                  <div className="w-20">
                    <Input type="number" min="0" step="0.5" placeholder="Qty" data-testid="mreq-qty-input"
                      value={row.qty}
                      onChange={(e) => setRows(rows.map((r, i) => (i === idx ? { ...r, qty: e.target.value } : r)))} />
                  </div>
                  <Badge variant="outline" className="h-10 px-2 min-w-[48px] justify-center text-xs">{m ? m.unit : "—"}</Badge>
                  <Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, i) => i !== idx))} disabled={rows.length === 1}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              );
            })}
            <Button variant="outline" size="sm" className="gap-2" data-testid="mreq-add-item-button" onClick={() => setRows([...rows, { material_id: "", qty: "" }])}>
              <Plus className="h-4 w-4" /> Add material
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Remarks (optional)</Label>
            <Textarea data-testid="mreq-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="e.g. urgent for evening prep" />
          </div>
          <Button onClick={submit} disabled={loading} className="gap-2 w-full" data-testid="mreq-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Request
          </Button>
        </div>
      </Card>

      <div className="mobile-compact" data-testid="mreq-team-requests-section">
        <h3 className="font-heading font-bold text-lg mb-4">Team Requests</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card className="p-3 border-orange-100 dark:border-slate-800" data-testid="mreq-total-issued">
            <p className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">Total Issued</p>
            <p className="font-mono text-lg font-extrabold">{fmtMoney(totals.issued)}</p>
          </Card>
          <Card className="p-3 border-orange-100 dark:border-slate-800" data-testid="mreq-total-received">
            <p className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">Total Received</p>
            <p className="font-mono text-lg font-extrabold text-emerald-700 dark:text-emerald-400">{fmtMoney(totals.received)}</p>
          </Card>
        </div>
        <div className="space-y-3" data-testid="mreq-list">
          {requests.length === 0 && <Card className="p-8 text-center text-muted-foreground">No material requests yet.</Card>}
          {requests.map((r) => (
            <RequestCard key={r.id} r={r} onEdit={openEdit} onChanged={loadRequests} />
          ))}
        </div>
      </div>

      {/* Edit request dialog */}
      <Dialog open={!!editReq} onOpenChange={(v) => !v && setEditReq(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Material Request</DialogTitle>
            <DialogDescription>Editing allowed until the store issues this request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Store</Label>
              <div className="h-10 flex items-center px-3 rounded-md border border-border bg-secondary font-semibold" data-testid="mreq-edit-store-display">
                {editReq?.store} Store
              </div>
            </div>
            <div className="space-y-2">
              <Label>Required By</Label>
              <Input type="date" min={todayStr()} value={editRequiredBy} onChange={(e) => setEditRequiredBy(e.target.value)} className="w-48 font-mono" data-testid="mreq-edit-required-by-input" />
            </div>
            <div className="space-y-3">
              <Label>Materials</Label>
              {editRows.map((row, idx) => {
                const m = editMats.find((x) => x.id === row.material_id) || editReq?.items.find((x) => x.material_id === row.material_id);
                const unit = m?.unit || "—";
                return (
                  <div key={idx} className="flex flex-wrap sm:flex-nowrap items-end gap-2" data-testid="mreq-edit-item-row">
                    <div className="basis-full sm:basis-0 sm:flex-1 min-w-0">
                      <MaterialCombobox
                        materials={editMats}
                        value={row.material_id}
                        testid="mreq-edit-material-combobox"
                        onChange={(v) => setEditRows(editRows.map((r, i) => (i === idx ? { ...r, material_id: v } : r)))}
                      />
                    </div>
                    <div className="w-20">
                      <Input type="number" min="0" step="0.5" placeholder="Qty" data-testid="mreq-edit-qty-input"
                        value={row.qty}
                        onChange={(e) => setEditRows(editRows.map((r, i) => (i === idx ? { ...r, qty: e.target.value } : r)))} />
                    </div>
                    <Badge variant="outline" className="h-10 px-2 min-w-[48px] justify-center text-xs">{unit}</Badge>
                    <Button variant="ghost" size="icon" onClick={() => setEditRows(editRows.filter((_, i) => i !== idx))} disabled={editRows.length === 1}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                );
              })}
              <Button variant="outline" size="sm" className="gap-2" data-testid="mreq-edit-add-item-button" onClick={() => setEditRows([...editRows, { material_id: "", qty: "" }])}>
                <Plus className="h-4 w-4" /> Add material
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea value={editRemarks} onChange={(e) => setEditRemarks(e.target.value)} rows={2} data-testid="mreq-edit-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={editSaving} className="gap-2" data-testid="mreq-edit-save-button">
              {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestCard({ r, onEdit, onChanged }) {
  const [recv, setRecv] = useState(() => Object.fromEntries(r.items.map((it) => [it.material_id, it.issued_qty ?? it.qty])));
  const [touched, setTouched] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const issuedAmt = r.items.reduce((s, it) => s + (it.issued_qty || 0) * (it.rate || 0), 0);
  const receivedAmt = r.items.reduce((s, it) => s + (it.received_qty || 0) * (it.rate || 0), 0);
  const hasDiscrepancy = r.status === "Received" && r.items.some((it) => (it.received_qty || 0) !== (it.issued_qty || 0));

  useEffect(() => {
    if (touched) return;
    setRecv(Object.fromEntries(r.items.map((it) => [it.material_id, it.issued_qty ?? it.qty])));
  }, [r, touched]);

  const markReceived = async () => {
    setBusy(true);
    try {
      await api.patch(`/material-requests/${r.id}/receive`, {
        items: r.items.map((it) => ({ material_id: it.material_id, qty: Number(recv[it.material_id] || 0) })),
        comment,
      });      toast.success("Marked as received");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 border-orange-100 dark:border-slate-800 fade-up" data-testid="mreq-card">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="font-semibold">{r.store} Store</p>
          <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
          {r.required_by && <p className="text-xs font-semibold text-primary" data-testid="mreq-card-required-by">Required by {fmtDay(r.required_by)}</p>}
        </div>
        <div className="flex items-center gap-1.5">
          {hasDiscrepancy && <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300 text-[10px]" data-testid="mreq-discrepancy-badge">Short</Badge>}
          <Badge variant="outline" className={statusCfg[r.status]} data-testid="mreq-status-badge">{r.status}</Badge>
        </div>
      </div>

      <div className="text-sm space-y-1.5 mb-3">
        {(r.status === "Issued" || r.status === "Received") && (
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[10px] uppercase font-mono tracking-wider text-muted-foreground pb-1 border-b border-border">
            <span>Material</span>
            <span className="text-right">Requested</span>
            <span className="text-right">Issued</span>
            <span className="text-right w-20">Received</span>
          </div>
        )}
        {r.items.map((it, i) => {
          const issued = it.issued_qty ?? 0;
          if (r.status === "Issued") {
            return (
              <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                <span className="truncate">{it.name}</span>
                <span className="font-mono text-muted-foreground text-right">{it.qty} {it.unit}</span>
                <span className="font-mono font-semibold text-right">{issued} {it.unit}</span>
                <Input
                  type="number" min="0" max={issued} step="0.5"
                  className="h-8 w-20 text-right font-mono"
                  data-testid="mreq-received-qty-input"
                  value={recv[it.material_id] ?? ""}
                  onChange={(e) => { setTouched(true); setRecv({ ...recv, [it.material_id]: e.target.value }); }}
                />
              </div>
            );
          }
          if (r.status === "Received") {
            return (
              <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                <span className="truncate">{it.name}</span>
                <span className="font-mono text-muted-foreground text-right">{it.qty} {it.unit}</span>
                <span className="font-mono text-right">{issued} {it.unit}</span>
                <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400 text-right w-20">{it.received_qty ?? 0} {it.unit}</span>
              </div>
            );
          }
          return (
            <div key={i} className="flex justify-between">
              <span className="truncate">{it.name}</span>
              <span className="font-mono text-muted-foreground">{it.qty} {it.unit}</span>
            </div>
          );
        })}
      </div>

      {r.remarks && <p className="text-xs italic text-muted-foreground border-t pt-2 mt-1">“{r.remarks}”</p>}
      {r.status === "Issued" && (
        <div className="mt-2">
          <Textarea data-testid="mreq-receive-comment" value={comment} onChange={(e) => { setTouched(true); setComment(e.target.value); }} rows={2} placeholder="Comment: any problem with the material received?" className="text-sm" />
        </div>
      )}
      {r.received_comment && <p className="text-xs text-rose-700 dark:text-rose-300 border-t pt-2 mt-1"><span className="font-semibold">Receipt note:</span> “{r.received_comment}”</p>}
      <HistoryStamps history={r.history} fallbackAction="Requested" fallbackName={r.requested_by_name} fallbackAt={r.created_at} testid="mreq-card-history" />

      <div className="mt-3 flex items-center justify-between gap-2">
        <div>
          {r.status === "Pending" && (
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="mreq-edit-button" onClick={() => onEdit(r)}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
          {r.status === "Issued" && (
            <Button size="sm" className="gap-1.5" data-testid="mreq-receive-button" onClick={markReceived} disabled={busy}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PackageCheck className="h-3.5 w-3.5" />} Received
            </Button>
          )}
        </div>
        {(r.status === "Issued" || r.status === "Received") && (
          <div className="text-right text-xs leading-tight">
            <p className="text-muted-foreground">Issued <span className="font-mono font-semibold text-foreground" data-testid="mreq-card-issued-amount">{fmtMoney(issuedAmt)}</span></p>
            {r.status === "Received" && <p className="text-muted-foreground">Received <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400" data-testid="mreq-card-received-amount">{fmtMoney(receivedAmt)}</span></p>}
          </div>
        )}
      </div>
    </Card>
  );
}

export function MaterialCombobox({ materials, value, onChange, testid = "mreq-material-combobox" }) {
  const [open, setOpen] = useState(false);
  const selected = materials.find((m) => m.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid={testid} className="w-full justify-between font-normal">
          <span className="truncate">{selected ? selected.name : "Choose material"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search material..." />
          <CommandList>
            <CommandEmpty>No material found.</CommandEmpty>
            <CommandGroup>
              {materials.map((m) => (
                <CommandItem key={m.id} value={m.name} onSelect={() => { onChange(m.id); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === m.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1 truncate">{m.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">{m.stock} {m.unit}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
